// GES-29 Phase 0 — shared document scaffold. On screen it's an overlay preview; PRINT renders the SERVER PDF (47263 K-S3: the
// server PDF is the one true page geometry — footer in the bottom margin of every page, real {page}/{pages}); the clean popup is the fallback
// when the server PDF is unavailable or there is no document id. A1-neutral: pure render — posts nothing durable.
// 47263 (K-S3): when the API gives a page `frame` { top, bottom, header_html, footer_html } (the SAME strings + effective margins the server PDF and the
// Settings preview use), the sheet lifts the header/footer OUT of the body and draws them ONCE PER PAGE (the in-body .doc-tpl-screen copies are hidden),
// so the on-screen sheet == the PDF page geometry. Without a frame it falls back to the old continuous-sheet model.
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IcoLn } from '../../shell/ActionIcons';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import i18n from '../../i18n';
import { savePdf, saveBlob } from './savePdf';
import api from '../../services/api';
import { getDocPaper, paperCssVars, paperGeometry } from '../../utils/docPaper';
import DocPrintLogDrawer from './DocPrintLogDrawer';
import { PX_PER_MM, framePages, framePieceStyle, counters } from './docFrameGeom';
import { useDocFont } from './docFont';
import './documents.css';

// Collect the .doc-* visual rules from the loaded stylesheets (skip @media blocks — those are the on-page
// Cmd+P fallback and must not leak into the iframe) so the printed document looks identical to the preview.
function collectDocCss() {
  let css = '';
  for (const ss of Array.from(document.styleSheets)) {
    let rules;
    try { rules = ss.cssRules; } catch { continue; }   // cross-origin sheet — skip
    for (const r of Array.from(rules || [])) {
      if (r.type === 4 /* CSSMediaRule */) continue;
      const t = r.cssText || '';
      if (t.includes('.doc-')) css += t + '\n';
    }
  }
  return css;
}

const SAFE = /^[A-Za-z0-9_:.-]{1,80}$/;   // the PDF sanitiser's data-* value shape

// `doc` = { type, id } names the document for the server PDF ticket (type: invoice|proforma|quote|statement|bl|monthly). Without it only Print (popup) is offered.
// Two explicit actions: PRINT = the SERVER PDF printed from a clean tab/iframe (popup fallback); SAVE PDF = the server-rendered PDF opened/downloaded.
// The preview is the PAPER: the sheet has the page width, a min-height of whole pages, page guides, and scales down to fit narrow screens.
// vars = { no, date } -> data-doc-no / data-doc-date (the template's {document_number}/{date} tokens in the server PDF header/footer).
// paper = the document template's page for this document; absent = getDocPaper(doc.type). frame = the per-page header/footer + effective margins (47263).
// verify = { id, run?(lang) -> Promise (default api.verifyInvoice; sales documents pass api.verifySalesDocument) } (the issued invoice/proforma/correction-CN UUID) adds a Verify button that recomputes the server PDF SHA-256 and compares it to the one pinned at issue (47252/47260).
export default function DocumentSheet({ onClose, doc, vars: docVars, paper: tplPaper, frame, verify, printLog, pdfFetch, children }) {   // pdfFetch(lang) -> Blob: the logged server PDF of this document by id (47283d: the sales documents)
  const { t } = useTranslation('common');
  const overlayRef = useRef(null);
  const sheetRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [pages, setPages] = useState(1);
  const [deskH, setDeskH] = useState(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [logOpen, setLogOpen] = useState(false);   // 47264: the print HISTORY opens on demand in its own drawer, never on the document
  const [badge, setBadge] = useState(null);         // 47264c: { marked, mention } of this document's LAST pdf render (drives the Duplicata/Copie/Original badge)
  const [logTick, setLogTick] = useState(0);        // bumped after a Save/Print so the badge + drawer re-read the log
  // 47264c: invoice/proforma Save PDF + Print fetch the SERVER PDF by id (GET /sales-invoices/:id/pdf) — the route that LOGS the render and stamps the reprint mark. Web-drawn
  // documents (quote/statement/bl) have no such route and keep the posted-body render (/render/pdf). Without this the modal never hit the logging route, so Duplicata never appeared.
  // the logging-by-id route is used ONLY for an issued document addressed by its OWN id — signalled by printLog.id === doc.id (InvoiceSheet). The order-level preview (InvoiceDocument,
  // doc.id = operationId, no printLog) and the web-drawn documents keep the posted-body render, so nothing regresses.
  const serverPdfKind = !!pdfFetch || ((doc?.type === 'invoice' || doc?.type === 'proforma') && !!printLog?.id && String(printLog.id) === String(doc?.id));
  useEffect(() => {   // 47264c: the badge = this document's LAST pdf render (newest-first rows); mention = the type's current Duplicata setting
    if (!printLog?.id) { setBadge(null); return undefined; }
    let alive = true;
    api.getDocPrintLog(printLog.kind, printLog.id)
      .then((r) => { if (!alive) return; const lp = (r.rows || []).find((x) => x.channel === 'pdf'); setBadge(lp ? { marked: !!lp.is_reprint, mention: r.mention || null } : null); })
      .catch(() => {});
    return () => { alive = false; };
  }, [printLog?.id, printLog?.kind, logTick]);   // eslint-disable-line react-hooks/exhaustive-deps
  const paper = useMemo(() => (tplPaper && tplPaper.pageSize ? { pageSize: tplPaper.pageSize, orientation: tplPaper.orientation, margins: tplPaper.margins } : getDocPaper(doc?.type)), [doc?.type, tplPaper]);
  const geo = useMemo(() => paperGeometry(paper), [paper]);
  // 47263: ONE shared page geometry — the frame's effective (widened) top/bottom margins, else the raw paper margins
  const framed = !!(frame && frame.top != null && frame.bottom != null);
  const effTop = framed ? frame.top : geo.margins.top;
  const effBottom = framed ? frame.bottom : geo.margins.bottom;
  const contentHmm = Math.max(1, geo.heightMm - effTop - effBottom);
  const vars = useMemo(() => {
    const v = paperCssVars(paper);
    return framed ? { ...v, '--m-top': `${effTop}mm`, '--m-bottom': `${effBottom}mm`, '--paper-content-h': `${contentHmm}mm` } : v;
  }, [paper, framed, effTop, effBottom, contentHmm]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && onClose) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Whole pages: measure the content, grow the sheet to ceil(content / content-height) pages; fit the width on narrow screens.
  useLayoutEffect(() => {
    const sheet = sheetRef.current; const overlay = overlayRef.current;
    if (!sheet || !overlay) return undefined;
    const measure = () => {
      // measure the last BODY child, skipping the per-page frame overlay we draw
      const kids = Array.from(sheet.children).filter((c) => !c.classList || !c.classList.contains('doc-frame'));
      const last = kids[kids.length - 1];
      const contentPx = last ? last.offsetTop + last.offsetHeight - effTop * PX_PER_MM : 0;
      setPages(framePages(contentPx, contentHmm));
      const avail = Math.max(120, overlay.clientWidth - 32);
      setScale(Math.min(1, avail / (geo.widthMm * PX_PER_MM)));
      setDeskH(sheet.offsetHeight);
    };
    measure();
    const ro = new ResizeObserver(measure); ro.observe(overlay); ro.observe(sheet);
    const mo = new MutationObserver(measure); mo.observe(sheet, { childList: true, subtree: true, characterData: true });
    return () => { ro.disconnect(); mo.disconnect(); };
  }, [geo, effTop, contentHmm]);

  // The body posted to the server renderer: the sheet minus <style> (the sanitiser refuses it), the in-body template header/footer copies
  // (.doc-tpl-screen) and the on-screen frame overlays (.doc-frame) — the server frame redraws them on every page.
  function postBody() {
    const el = sheetRef.current; if (!el) return null;
    const body = el.cloneNode(true);
    body.querySelectorAll('style, .doc-tpl-screen, .doc-frame').forEach((x) => x.remove());
    body.classList.remove('framed');   // the on-screen page-model class is not template vocabulary: the PDF sanitiser refuses it (PDF_BAD_MARKUP), so Save PDF / Print of a web-drawn document never reached the server PDF (found by 47307)
    return body.outerHTML;
  }

  // Print-popup fallback: the clean popup/iframe (used only when the server PDF is unavailable). Never touches the server.
  function docHtml(el) {
    const fontFamily = getComputedStyle(el).fontFamily || getComputedStyle(document.body).fontFamily || 'system-ui, sans-serif';   // 47283: the sheet's own family (the document font when set)
    const faces = Array.from(document.querySelectorAll('style[data-doc-font]')).map((x) => x.textContent).join('');
    const m = geo.margins;
    const clone = el.cloneNode(true); clone.querySelectorAll('.doc-frame').forEach((x) => x.remove());
    return '<!doctype html><html><head><meta charset="utf-8"><title>Document</title><style>'
      + `@page{size:${geo.widthMm}mm ${geo.heightMm}mm;margin:${m.top}mm ${m.right}mm ${m.bottom}mm ${m.left}mm}`
      + 'html,body{margin:0;background:#fff;height:auto!important;overflow:visible!important;position:static!important;display:block!important}'
      + faces + 'body{font-family:' + fontFamily + '}'
      + collectDocCss()
      + '.doc-sheet{box-shadow:none!important;width:auto!important;max-width:none!important;min-height:0!important;height:auto!important;overflow:visible!important;position:static!important;border-radius:0!important;padding:0!important;background-image:none!important}'
      + '.doc-sheet.framed .doc-tpl-screen{display:revert!important}'   // in the popup the in-body copies print (no server frame here)
      + `.doc-bl-wm{background-size:auto ${geo.contentHeightMm}mm!important}`
      + '.doc-table{break-inside:auto}.doc-table thead{display:table-header-group}.doc-table tfoot{display:table-row-group}.doc-table tr{break-inside:avoid;page-break-inside:avoid}'
      + '</style></head><body>' + clone.outerHTML + '</body></html>';
  }
  function printPopup() {
    const el = sheetRef.current;
    if (!el) { window.print(); return; }
    const html = docHtml(el);
    const pop = window.open('', '_blank');
    if (pop) {
      pop.document.open(); pop.document.write(html); pop.document.close();
      pop.addEventListener('afterprint', () => pop.close());
      setTimeout(() => { pop.focus(); pop.print(); }, 300);
      return;
    }
    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:210mm;height:400px;border:0;background:#fff;';
    document.body.appendChild(iframe);
    const idoc = iframe.contentWindow.document;
    idoc.open(); idoc.write(html); idoc.close();
    const go = () => {
      try { iframe.style.height = Math.max(idoc.documentElement.scrollHeight, idoc.body.scrollHeight, 400) + 'px'; } catch { /* ignore */ }
      try { iframe.contentWindow.focus(); iframe.contentWindow.print(); }
      finally { setTimeout(() => iframe.remove(), 1000); }
    };
    if (idoc.readyState === 'complete') setTimeout(go, 300);
    else iframe.onload = () => setTimeout(go, 300);
  }

  // PRINT (47263): the SERVER PDF — the true page geometry (footer in every page's bottom margin, real counters). Printed from a tab/iframe.
  // 47264c: invoice/proforma -> the SERVER PDF by id (logs + stamps the reprint mark); every other kind -> the posted-body render. A promise of the PDF Blob.
  const loggedPdf = () => (pdfFetch ? pdfFetch(i18n.language) : api.getInvoicePdf(String(doc.id), i18n.language));
  const fetchServerPdf = () => (serverPdfKind
    ? loggedPdf()
    : api.renderPdf({ docType: doc.type, docId: String(doc.id), css: collectDocCss(), body: postBody(), lang: i18n.language, paper, filename: `${doc.type}-${doc.id}` }));
  const printBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) { w.addEventListener('load', () => { try { w.focus(); w.print(); } catch { /* user prints from the viewer */ } }); }
    else {
      const iframe = document.createElement('iframe'); iframe.setAttribute('aria-hidden', 'true');
      iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:0;height:0;border:0;'; iframe.src = url;
      document.body.appendChild(iframe); iframe.onload = () => { try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* ignore */ } setTimeout(() => iframe.remove(), 60000); };
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };
  function printNow() {
    if (!doc?.type || !doc?.id || busy) { printPopup(); return; }
    if (!serverPdfKind && !postBody()) { printPopup(); return; }   // no server ticket for a web-drawn doc: the clean popup
    setNote(''); setBusy(true);
    fetchServerPdf()
      .then((blob) => { printBlob(blob); setLogTick((x) => x + 1); })   // the GET /pdf logged this render (reprint from the 2nd pdf on)
      .catch((e) => { console.warn('[print] server PDF unavailable, falling back to popup:', e.code || e.message); printPopup(); })
      .finally(() => setBusy(false));
  }

  // SAVE PDF: the server PDF. invoice/proforma -> GET /sales-invoices/:id/pdf (logs + marks); else the posted-body render. Inline tab when the browser's PDF viewer is on, else a download.
  function saveNow() {
    if (!doc?.type || !doc?.id || busy) return;
    if (!serverPdfKind) {   // web-drawn: unchanged posted-body path
      const body = postBody(); if (!body) return;
      setNote(''); setBusy(true);
      savePdf({ docType: doc.type, docId: String(doc.id), css: collectDocCss(), body, lang: i18n.language, paper }, `${doc.type}-${doc.id}`)
        .catch((e) => { console.warn('[pdf] server render unavailable:', e.code || e.message); setNote(t('doc.saveFailed')); })
        .finally(() => setBusy(false));
      return;
    }
    setNote(''); setBusy(true);
    const inline = typeof navigator === 'undefined' || navigator.pdfViewerEnabled !== false;
    const pop = inline ? window.open('', '_blank') : null;   // popup opened synchronously in the click (popup-safe)
    loggedPdf()   // GET /sales-invoices/:id/pdf (or the sales document's by id) — logs the render + stamps the reprint mark
      .then((blob) => { if (pop && !pop.closed) { const u = URL.createObjectURL(blob); pop.location.href = u; const iv = setInterval(() => { if (pop.closed) done(); }, 2000); const done = () => { clearInterval(iv); clearTimeout(to); URL.revokeObjectURL(u); }; const to = setTimeout(done, 60000); } else saveBlob(blob, `${doc.type}-${doc.id}`); setLogTick((x) => x + 1); })
      .catch((e) => { if (pop) pop.close(); console.warn('[pdf] server render unavailable:', e.code || e.message); setNote(t('doc.saveFailed')); })
      .finally(() => setBusy(false));
  }

  // VERIFY (R-CA): the server recomputes the issued PDF's SHA-256 and compares it to the one pinned at issue. Read-only; posts nothing.
  function verifyNow() {
    if (!verify?.id || busy) return;
    setNote(''); setBusy(true);
    (verify.run ? verify.run(i18n.language) : api.verifyInvoice(String(verify.id), i18n.language))
      .then((r) => setNote(r.match === true ? t('doc.verifyOk') : r.match === false ? t('doc.verifyMismatch') : t('doc.verifyPinned')))
      .catch((e) => setNote(e && e.message ? e.message : t('doc.verifyFailed')))   // api.js localizes e.message (plainText / error_fr by locale)
      .finally(() => setBusy(false));
  }

  // the per-page header/footer overlays (47263): the PDF frame strings drawn once per page, {page}/{pages} filled per copy; aria-hidden (decorative, the body carries the real content)
  const framePiece = (part, i) => (
    <div key={`${part}${i}`} className="doc-frame-piece" data-tpl={`frame-${part}`} aria-hidden="true"
      style={framePieceStyle(part, i, pages, geo.heightMm)}
      dangerouslySetInnerHTML={{ __html: counters(frame[`${part}_html`], i + 1, pages) }} />
  );

  // physical-page model when framed: min-height = whole pages, the frame drawn per page (the continuous-sheet guide is off); else the old model
  const docFont = useDocFont(frame && frame.font);   // 47283: the document template's font (absent = a document frozen before it: the sheet keeps its look)
  const sheetStyle = framed || docFont ? { ...(docFont ? { fontFamily: docFont, fontVariantNumeric: 'tabular-nums' } : {}), ...(framed ? { minHeight: `${pages * geo.heightMm}mm`, padding: `${effTop}mm ${geo.margins.right}mm ${effBottom}mm ${geo.margins.left}mm`, backgroundImage: 'none' } : {}) } : undefined;

  return createPortal(
    <div className="doc-overlay" role="dialog" aria-modal="true" aria-label="Document preview" ref={overlayRef} style={{ ...vars, '--doc-pages': pages }}>
      <div className="doc-toolbar doc-noprint">
        <button type="button" className="doc-btn primary" data-testid="doc-print" disabled={busy} onClick={printNow}><IcoLn name="print" /> {t('doc.print')}</button>
        {doc?.type && doc?.id && <button type="button" className="doc-btn primary" data-testid="doc-save-pdf" disabled={busy} onClick={saveNow}><IcoLn name="download" /> {t('doc.savePdf')}</button>}
        {verify?.id && <button type="button" className="doc-btn ghost" data-testid="doc-verify" disabled={busy} onClick={verifyNow}><IcoLn name="shield" /> {t('doc.verify')}</button>}
        {printLog?.id && <button type="button" className="doc-btn ghost" data-testid="doc-print-log-open" onClick={() => setLogOpen(true)}><IcoLn name="clipboard" /> {t('doc.printLog')}</button>}
        <button type="button" className="doc-btn ghost" onClick={() => onClose && onClose()}>{t('doc.close')}</button>
        {badge && <span className={`doc-badge${badge.marked ? ' dup' : ''}`} data-testid="doc-dup-badge" data-marked={badge.marked ? '1' : '0'} role="status">
          {badge.marked ? (badge.mention === 'copie' ? t('doc.badgeCopie') : t('doc.badgeDuplicata')) : t('doc.badgeOriginal')}</span>}
        {note && <span className="doc-note-bar" role="status">{note}</span>}
      </div>
      <div className="doc-desk" style={{ width: `calc(var(--paper-w) * ${scale})`, height: deskH ? deskH * scale : undefined }}>
        <div className="doc-desk-inner" style={{ transform: `scale(${scale})` }}>
          <article className={`doc-sheet${framed ? ' framed' : ''}${docFont ? ' doc-font' : ''}`} ref={sheetRef} style={sheetStyle} data-doc={doc?.type && doc?.id ? `${doc.type}:${doc.id}` : undefined}
            data-doc-no={docVars && SAFE.test(docVars.no || '') ? docVars.no : undefined} data-doc-date={docVars && /^\d{4}-\d{2}-\d{2}$/.test(docVars.date || '') ? docVars.date : undefined}>
            {children}
            {framed && (frame.header_html || frame.footer_html) && <div className="doc-frame" aria-hidden="true">
              {Array.from({ length: pages }, (_, i) => [
                frame.header_html ? framePiece('header', i) : null,
                frame.footer_html ? framePiece('footer', i) : null,
                i ? <div key={`brk${i}`} className="doc-frame-break" style={{ top: `${i * geo.heightMm}mm` }} /> : null,
              ])}
            </div>}
          </article>
        </div>
      </div>
      {logOpen && printLog?.id && <DocPrintLogDrawer kind={printLog.kind} id={printLog.id} title={docVars && docVars.no ? docVars.no : undefined} onClose={() => setLogOpen(false)} />}
    </div>,
    document.body,
  );
}
